import { INGLIS_BLUE } from '../../constants/colors'
import { useCallback } from 'react'
import { useMapContext } from '../../context/MapContext'
import { useFilteredSites, isCategoryVisible } from '../../hooks/useFilteredSites'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import SiteListCard from './SiteListCard'
import ReuseNetworkRow from './ReuseNetworkRow'
import SitePanel from '../SitePanel/SitePanel'
import sitesData from '../../data/sites.json'
import { prefersReducedMotion } from '../../utils/motion'
import { fullExpandWidth } from '../../utils/listPanelWidth'
import { REUSE_NETWORK_SITE } from '../../constants/reuseNetworkSite'
import type { Site, SitesData } from '../../types'

const allSites: Site[] = (sitesData as SitesData)?.sites ?? []

const ExpandIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
    <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
  </svg>
)
const MinimizeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/>
    <line x1="10" y1="14" x2="3" y2="21"/><line x1="21" y1="3" x2="14" y2="10"/>
  </svg>
)
const CloseListIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
)

// 44px hit area, not the 24px WCAG 2.5.8 (AA) floor these previously sat exactly on — 2.5.5 (AAA)
// asks for 44, and the map markers already use it, so the two didn't agree. The icon inside stays
// 13px and `background: none` means the larger box is invisible until hover/focus, so this buys the
// target size without visually bulking up the panel header.
const iconBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: 6, minWidth: 44, minHeight: 44, borderRadius: 4, color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0 }

interface ListPanelProps {
  width: number
  onExpand: () => void
  onFullExpand?: () => void
  onMinimize?: () => void
}

export default function ListPanel({ width, onExpand, onFullExpand = () => {}, onMinimize = () => {} }: ListPanelProps) {
  const { selectedSite, setSelectedSite, filters, mapRef, setViewMode } = useMapContext()
  const filteredSites: Site[] = useFilteredSites(allSites, filters)
  const sortedSites = [...filteredSites].sort((a, b) => a.name.localeCompare(b.name))

  // Clearing the selection is what actually closes the detail view. Switching to map view alone
  // left selectedSite set, and App renders `{selectedSite && <SitePanel />}` on that branch — so
  // dismissing the list immediately re-opened the very same panel docked to the right of the map
  // instead of closing anything.
  function handleClose() {
    setSelectedSite(null)
    setViewMode('map')
  }

  const handleCardClick = useCallback((site: Site) => {
    setSelectedSite(site)
    const current = mapRef.current
    if (current) {
      current.flyTo({
        center: [site.lon, site.lat],
        zoom: Math.max(current.getZoom?.() ?? 9, 9),
        duration: prefersReducedMotion() ? 0 : 800,
      })
    }
  }, [setSelectedSite, mapRef])

  // Show the collapse icon once the panel is at (or dragged past) the width the maximize button
  // targets. Derived from the same fullExpandWidth() the button uses instead of a separate
  // hardcoded 55%-of-viewport threshold, so the two can't drift apart — with the old pair, a
  // maximize preset below 55% would have left the button stuck showing "Expand" after expanding.
  const windowWidth = useWindowWidth()
  const isFullyExpanded = width >= fullExpandWidth(windowWidth)

  if (width <= 32) {
    return (
      <button
        type="button"
        aria-label="Expand list panel"
        onClick={onExpand}
        style={{
          width: 32,
          flexShrink: 0,
          background: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
          border: 'none',
          borderRight: '1px solid #e2e8f0',
        }}
      >
        <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap' }}>
          ▶ List
        </span>
      </button>
    )
  }

  function handleBack() {
    setSelectedSite(null)
  }

  return (
    <div
      // Target of the "Site list" skip link in App's skip navigation, and a named landmark so
      // screen-reader users can also reach it from their own rotor/landmark list rather than only
      // from the top of the page. tabIndex={-1} is what lets the link actually move focus here.
      id="site-list"
      tabIndex={-1}
      role="region"
      aria-label="Site list"
      style={{ width: `min(${width}px, 100vw)`, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid #e2e8f0', background: '#fff', overflow: 'hidden' }}
    >
      {selectedSite ? (
        <>
          <div style={{ padding: '8px 10px 8px 14px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              type="button"
              onClick={handleBack}
              aria-label="Back to all sites"
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: INGLIS_BLUE, fontWeight: 600, fontSize: '0.85rem', padding: 0 }}
            >
              ← All sites
            </button>
            <div style={{ display: 'flex', gap: 2 }}>
              <button
                type="button"
                onClick={isFullyExpanded ? onMinimize : onFullExpand}
                title={isFullyExpanded ? 'Restore default size' : 'Expand list'}
                aria-label={isFullyExpanded ? 'Collapse list panel' : 'Expand list panel'}
                style={iconBtnStyle}
              >
                {isFullyExpanded ? <MinimizeIcon /> : <ExpandIcon />}
              </button>
              <button type="button" onClick={handleClose} title="Close list" aria-label="Close list panel" style={iconBtnStyle}><CloseListIcon /></button>
            </div>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <SitePanel site={selectedSite} fluidWidth />
          </div>
        </>
      ) : (
        <>
          <div style={{ padding: '8px 10px 8px 14px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* Deliberately not a live region. MapContainer already owns one announcing
                "N of M sites" on every filter change, and the map is mounted alongside this panel
                in list view — two live regions on the same event meant the change was announced
                twice. This stays as plain visible text. */}
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)' }}>
              {filteredSites.length} SITES
            </span>
            <div style={{ display: 'flex', gap: 2 }}>
              <button
                type="button"
                onClick={isFullyExpanded ? onMinimize : onFullExpand}
                title={isFullyExpanded ? 'Restore default size' : 'Expand list'}
                aria-label={isFullyExpanded ? 'Collapse list panel' : 'Expand list panel'}
                style={iconBtnStyle}
              >
                {isFullyExpanded ? <MinimizeIcon /> : <ExpandIcon />}
              </button>
              <button type="button" onClick={handleClose} title="Close list" aria-label="Close list panel" style={iconBtnStyle}><CloseListIcon /></button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {sortedSites.map(site => (
              <SiteListCard
                key={site.id}
                site={site}
                isSelected={(selectedSite as Site | null)?.id === site.id}
                onClick={handleCardClick}
              />
            ))}
            {isCategoryVisible(filters, 'TECHOWL') && (
              <ReuseNetworkRow
                isSelected={(selectedSite as Site | null)?.id === REUSE_NETWORK_SITE.id}
                onClick={() => handleCardClick(REUSE_NETWORK_SITE)}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}
